class MapService{
    constructor(CoreCompanyService, CoreUserService, $q){
        this.mapBoxBounds = {};
        this.mapSearchText = '';
        this.dragListeners = [];
        this.CoreCompanyService = CoreCompanyService;
        this.CoreUserService = CoreUserService;

        // the interaction panel has 5 states
        this.SHOW_BUTTON = 'button';
        this.LOADING_TEXT = 'loading';
        this.HELPER_TEXT = 'text';
        this.RESULTS_COUNT = 'count';
        this.BLANK = '';
        this.interactionState = this.HELPER_TEXT;
        this.isMapSupported = true;
        this.shouldUpdateCount = true;
        this.$q = $q;

        // the default map viewport bounds
        this.DEFAULT_VIEWPORT_BOUNDS = {
            bounds: {
                _ne: {
                    lat: 53.3308729830153,
                    lng: 112.5000000002348
                },
                _sw: {
                    lat: -53.33087298302015,
                    lng: -112.49999999997999
                }
            },
            viewport: {
                bottomRight: {
                    lat: -53.33087298302015,
                    lon: 112.5000000002348
                },
                topLeft: {
                    lat: 53.3308729830153,
                    long: -112.49999999997999
                }
            }
        };

        // Layers in mapbox to update to get the full translation we want
        this.MAPBOX_LAYERS_TO_UPDATE_LANGUAGE = [
            'housenum-label',
            'waterway-label',
            'road-label-small',
            'road-label-medium',
            'road-label-large',
            'rail-label',
            'water-label-sm',
            'water-label',
            'airport-label',
            'marine-label-sm-ln',
            'marine-label-sm-pt',
            'marine-label-md-ln',
            'marine-label-md-pt',
            'marine-label-lg-ln',
            'marine-label-lg-pt',
            'state-label-sm',
            'state-label-md',
            'state-label-lg',
            'country-label-sm',
            'country-label-md',
            'country-label-lg',
            'place-city-lg-n',
            'place-city-lg-s',
            'place-city-md-n',
            'place-city-md-s',
            'place-city-sm',
        ];
    }

    initialize(contactSourceServiceViewportBounds){
        if(contactSourceServiceViewportBounds) {
            this.viewportBounds = new ViewportBounds(contactSourceServiceViewportBounds.bounds, contactSourceServiceViewportBounds.viewport);
        }
    }

    // Grab the users language if it is compliant with mapbox
    getUsersMBLanguage() {
        var deferred = this.$q.defer();

        this.CoreUserService.getCurrent(true).then(function getUser(){
            var userLanguage = this.CoreUserService.getLanguage(),
                supportedMapBoxLanguages = ['en', 'es', 'fr', 'de', 'ru', 'zh', 'zh-hans', 'pt', 'ar'];

            // translate our chinese simplified code to mapbox' chinese simplified code
            if(userLanguage === 'zh_CN') {
                userLanguage = 'zh-Hans';
            } else if(userLanguage === 'zh_TW') {
                userLanguage = 'zh';
            }

            if (supportedMapBoxLanguages.includes(userLanguage)) {
                deferred.resolve(userLanguage);
            } else {
                deferred.resolve(false);
            }
        }.bind(this));

        return deferred.promise;
    }

    registerMap(map){
        this.map = map;
    }

    resetMap() {
        if (this.map) {
            this.setInteractionState(this.HELPER_TEXT);
        }
    }

    // -------------------------------
    // Search Logic
    // -------------------------------

    triggerSearch() {
        this.refreshSearch();
    } registerRefreshSearch(refreshSearchFunction) {
        this.refreshSearch = refreshSearchFunction;
    }

    clear() {
        if (this.viewportBounds) {
            this.cachedViewportBounds = this.viewportBounds;
        }
        delete this.viewportBounds;
        delete this.yesTheMapIsDirty;
        this.setGeoCoderText('');
        this.resetModalCountComponent();
    }

    // -------------------------------
    // Bounds Selection
    // -------------------------------

    boundsSelected(bounds) {
        try {
            this.viewportBounds = new ViewportBounds(bounds);
        }
        catch(err) { // jshint ignore:line
            this.viewportBounds = undefined;
        }
    }

    getViewportBounds() {
        return this.viewportBounds;
    }

    getCachedViewportBounds() {
        if (this.viewportBounds) {
            return this.viewportBounds;
        }
        return this.cachedViewportBounds;
    }

    getCachedBoundsAsArray() {
        var bounds;
        if (this.cachedViewportBounds) {
            bounds = this.cachedViewportBounds.bounds;
            return [[bounds._sw.lng, bounds._sw.lat],[bounds._ne.lng, bounds._ne.lat]];
        }
        return null;
    }

    // -------------------------------
    // Modal Count component
    // -------------------------------

    resetModalCountComponent() {
        this.mapDataPointsCount = undefined;
    }

    getMapModalCountComponentValue() {
        return this.mapDataPointsCount;
    } setMapModalCountComponent(count) {
        this.mapDataPointsCount = count;
    }

    getShouldUpdateCount() {
        return this.shouldUpdateCount;
    }

    setShouldUpdateCount(bool) {
        this.shouldUpdateCount = bool;
    }

    // -------------------------------
    // Map functions
    // -------------------------------

    registerMiniMapBoundsSetter(boundsSetter) {
        // This is how we get at the minimap, since it is the first defined map
        if(!!!this.miniMapBoundsSetter) {
            this.miniMapBoundsSetter = boundsSetter;
        }
    } registerMiniMapBoundsGetter(boundsGetter) {
        // This is how we get at the minimap, since it is the first defined map
        if(!!!this.miniMapBoundsGetter) {
            this.miniMapBoundsGetter = boundsGetter;
        }
    }

    setMiniMapBounds() {
        if(this.miniMapBoundsSetter) {
            this.miniMapBoundsSetter(this.viewportBounds.getBounds());
        }
    }

    getScaleUnits() {
        return this.CoreCompanyService.getCurrent(true).then(currentCompany => {
            return ((currentCompany.country === 'us') ? 'imperial' : 'metric');
        });
    }

    // -------------------------------
    // GeoCoder (In map Search Bar) Logic
    // -------------------------------

    getGeoCoderText() {
        return this.mapSearchText;
    }

    setGeoCoderText(text) {
        this.mapSearchText = text;
    }

    registerUpdateInteractState(updateFunction) {
        this.updateInteractState = updateFunction;
    }

    getInteractionState() {
        return this.interactionState;
    }

    setInteractionState(state) {
        this.interactionState = state;
        // check if the map has become dirty
        if(state === this.RESULTS_COUNT) {
            this.yesTheMapIsDirty = true;
        }
        if(this.updateInteractState){
            this.updateInteractState();
        }
    }

    isMapDirty() {
        return this.yesTheMapIsDirty || false;
    }

    registerLocationFilter(locFunct) {
        this.locationFilterVar = locFunct;
    } isLocationFilterDirty() {
        return this.locationFilterVar.getQuerySelectionCount() > 0;
    }
}

angular.module('MW.Influencers.MapService', ['MW.Framework.CoreCompanyService', 'MW.Framework.CoreUserService'])
.service('MapService', [
    'CoreCompanyService',
    'CoreUserService',
    '$q',
    MapService
]);

// -------------------------------
// Viewport Bounds Class
// -------------------------------

class ViewportBounds {
    constructor(bounds, viewport) {
        this.bounds = bounds;
        if(viewport) {
            this.viewport = viewport;
        } else {
            try {
                this.viewport = this.translateBoundsToViewport();
            }
            catch (err) {
                this.viewport = this.translateArrayToViewport();
            }
        }
    }

    getViewport() {
        return this.filterViewport();
    }

    filterViewport() {
        var vp = this.viewport;

        // Bottom Right
        if(vp && vp.bottomRight) {
            // Lat
            if(vp.bottomRight.lat > 90) {
                vp.bottomRight.lat = 90;
            } else  if(vp.bottomRight.lat < -90) {
                vp.bottomRight.lat = -90;
            }
            // Lon
            if(vp.bottomRight.lon > 180) {
                vp.bottomRight.lon = 180;
            } else  if(vp.bottomRight.lon < -180) {
                vp.bottomRight.lon = -180;
            }
        }

        // Top Left
        if(vp && vp.topLeft) {
            // Lat
            if(vp.topLeft.lat > 90) {
                vp.topLeft.lat = 90;
            } else  if(vp.topLeft.lat < -90) {
                vp.topLeft.lat = -90;
            }
            // Lon
            if(vp.topLeft.lon > 180) {
                vp.topLeft.lon = 180;
            } else  if(vp.topLeft.lon < -180) {
                vp.topLeft.lon = -180;
            }
        }

        return vp;
    }

    translateBoundsToViewport() {
        return {
            topLeft: {
                lat: this.bounds.getNorthWest().lat,
                lon: this.bounds.getNorthWest().lng
            },
            bottomRight: {
                lat: this.bounds.getSouthEast().lat,
                lon: this.bounds.getSouthEast().lng
            }
        };
    }

    translateArrayToViewport() {
        return {
            topLeft: {
                lat: this.bounds[0][1],
                lon: this.bounds[0][0]
            },
            bottomRight: {
                lat: this.bounds[1][1],
                lon: this.bounds[1][0]
            }
        };
    }

    getBounds() {
        return this.bounds;
    }

    getBoundsAsArray(){
        return [[this.bounds._sw.lng, this.bounds._sw.lat],[this.bounds._ne.lng, this.bounds._ne.lat]];
    }
}
